<script setup lang="ts">
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import type { Company } from '@/types';
    import CustomerInvoices from './CustomerInvoices.vue';
    import { usePermissions } from '@/composables/usePermissions';
    import Permission from '@/enums/Permission';
    import { useDashboardStore } from '@/stores/dashboard';
    import CustomerDashboardStatItem from './CustomerDashboardStatItem.vue';

    const { t } = useI18n();
    const { hasPermission } = usePermissions();
    const dashboardStore = useDashboardStore();

    const props = defineProps<{
        company: Company;
    }>();

    const translatedUniqueWorkDays = computed(() => {
        return dashboardStore.uniqueWorkDays.map((day) => {
            return t(`task-templates.days_in_week.${day}`);
        });
    });

    const stats = computed(() => {
        return [
            {
                value: dashboardStore.procedureCount,
                description: t('dashboards.customer_stats.procedures'),
                icon: 'FileChartLine',
            },
            {
                value: t('dashboards.customer_stats.work_days', {
                    count: dashboardStore.uniqueWorkDays.length,
                }),
                description: t('dashboards.customer_stats.weekdays', {
                    days: translatedUniqueWorkDays.value.join(', '),
                }),
                icon: 'CircleArrowUp',
            },
            {
                value: props.company.vouchers_count,
                description: t('dashboards.customer_stats.missing_vouchers'),
                icon: 'Archive',
                href: hasPermission(Permission.VOUCHERS_VIEW)
                    ? route('v2.vouchers.index', { company_id: props.company.id })
                    : undefined,
            },
            {
                value: props.company.services?.vat_frequency
                    ? t(`companies.vat_frequency_options.${props.company.services.vat_frequency}`)
                    : '-',
                description: t('dashboards.customer_stats.vat_frequency'),
                icon: 'Calculator',
            },
            {
                value: dashboardStore.taskCount,
                description: t('dashboards.customer_stats.actual_tasks'),
                icon: 'ClipboardList',
            },
        ];
    });
</script>

<template>
    <div
        class="no-scrollbar grid w-full grid-cols-2 gap-4 overflow-x-auto overflow-y-hidden md:w-auto md:grid-cols-3 lg:shrink-0"
    >
        <CustomerInvoices :company="props.company" />
        <CustomerDashboardStatItem
            v-for="stat in stats"
            :stat="stat"
        />
    </div>
</template>
